/**
 * useConfigVersionBootstrap
 *
 * App 启动 / Clerk 鉴权完成后调用一次，完成：
 *   1. 检测当前用户/租户与本地 meta 是否一致 → 不一致清相应 scope 缓存
 *   2. 从 IndexedDB 灌注 Redux 内存（SWR：业务组件立刻能渲染）
 *   3. 异步拉 GET /system/config/versions，diff 本地 vs 服务端
 *   4. 不一致或缺失的资源 → 拉新数据 → 写回 IndexedDB + Redux + 更新 meta.versions
 *
 * 详见 OpenSpec add-version-based-cache。
 */

import { useEffect, useRef } from 'react';

import { configVersionsApi, type ConfigVersions } from '@/api/configVersionsApi';
import { useAppDispatch } from '@/hooks/redux';
import { hydrateFromCache, applyServerVersions } from '@/store/slices/lookupSlice';
import { loadUserMenus } from '@/store/slices/authSlice';
import { menuRelevantVersionsChanged } from '@/hooks/menuVersionsChanged';
import { getCacheStore } from '@/utils/cacheStore';
import { emitResourceVersionChanges } from '@/utils/configVersionEvents';

const META_KEY_USER = 'userId';
const META_KEY_TENANT = 'tenantId';
const META_KEY_VERSIONS = 'versions';

interface BootstrapOptions {
  userId?: string | null;
  tenantId?: string | null;
  /** Disable until rebuilt; useful while Clerk auth still loading. */
  enabled?: boolean;
}

/**
 * 一次性 bootstrap hook：useEffect 内部会等到 userId/tenantId 都就绪才跑。
 * 跑过一次就不再重跑（用 ref 保护）。
 */
export function useConfigVersionBootstrap(options: BootstrapOptions): void {
  const { userId, tenantId, enabled = true } = options;
  const dispatch = useAppDispatch();
  const ran = useRef(false);

  useEffect(() => {
    if (!enabled || !userId || !tenantId) return;
    if (ran.current) return;
    ran.current = true;

    void (async () => {
      const store = getCacheStore();

      try {
        // 1. 用户 / tenant 切换检测
        const prevUser = await store.get<string>('meta', META_KEY_USER);
        const prevTenant = await store.get<string>('meta', META_KEY_TENANT);

        if (prevTenant && prevTenant !== tenantId) {
          // 租户变了 → 清所有 tenant-scoped key
          await store.deleteMatching('lookups', (k) => k.includes(':tenant:'));
          await store.deleteMatching('meta', (k) => k.startsWith('versions'));
        } else if (prevUser && prevUser !== userId) {
          // 同租户用户变了 → 仅清 user-scoped key（tenant-scoped 保留复用）
          await store.deleteMatching('lookups', (k) => k.includes(':user:'));
        }

        await store.set('meta', META_KEY_USER, userId);
        await store.set('meta', META_KEY_TENANT, tenantId);

        // 2. 从 IndexedDB 灌注 Redux（SWR：先让 UI 渲染本地 stale 数据）
        const cached = await store.getAll<unknown>('lookups');
        if (cached.length > 0) {
          dispatch(hydrateFromCache(cached));
        }

        // 3. 异步拉服务端版本号
        let serverVersions: ConfigVersions;
        try {
          serverVersions = await configVersionsApi.getVersions();
        } catch (err) {
          console.warn('[bootstrap] failed to fetch /system/config/versions', err);
          return;  // 不阻塞 UI，下次路由 revalidator 重试
        }

        // 4. 比对本地 versions 后由 lookupSlice 决定哪些 resource 需重新拉
        //    （具体 fetch 由 useLookup 的下一次调用懒执行，或可在此预热高频项）
        const prevVersions = (await store.get<ConfigVersions>('meta', META_KEY_VERSIONS)) || {};
        dispatch(applyServerVersions({ prev: prevVersions, next: serverVersions }));
        // 不在 Redux 里的模块级缓存（表单目录、种类表单）也按配置版本失效
        emitResourceVersionChanges(prevVersions, serverVersions);
        // 菜单相关版本变化 → 立即重拉菜单树(覆盖"关页期间后端改了菜单,
        // 重开时 localStorage <1h 仍用旧树"的窗口);首次无快照不触发
        if (menuRelevantVersionsChanged(prevVersions, serverVersions)) {
          void dispatch(loadUserMenus());
        }
        await store.set('meta', META_KEY_VERSIONS, serverVersions);
      } catch (err) {
        console.error('[bootstrap] unexpected error', err);
      }
    })();
  }, [enabled, userId, tenantId, dispatch]);
}
